import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Clock, Loader2, Search, Users, FileText, ChevronLeft, ChevronRight,
  MessageSquare, Send, ArrowLeft, Pencil, BarChart3, Calendar
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const DAILY_TARGET = 480;
const WEEKLY_TARGET = 2400;

const formatMinutes = (m) => {
  if (!m) return '0h 0m';
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const TimesheetReview = () => {
  const [weeklySummary, setWeeklySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [commentText, setCommentText] = useState('');
  const [sendingComment, setSendingComment] = useState(false);
  const [adjustModal, setAdjustModal] = useState(null);
  const [adjustMinutes, setAdjustMinutes] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjusting, setAdjusting] = useState(false);
  const [stats, setStats] = useState({});

  const { getAuthHeaders, currentOffice } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const weekDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + weekOffset * 7);
    return d.toISOString().split('T')[0];
  }, [weekOffset]);

  useEffect(() => { fetchWeeklySummary(); fetchStats(); }, [currentOffice, weekOffset]);

  const fetchWeeklySummary = async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/timesheets/weekly-summary?week_of=${weekDate}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setWeeklySummary(data);
        // If an employee is selected, refresh their data too
        if (selectedEmployee) {
          const updated = data.employees?.find(e => e.employee_id === selectedEmployee.employee_id);
          if (updated) setSelectedEmployee(updated);
        }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      const res = await fetch(`${API_URL}/api/timesheets/summary`, { headers });
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error(err); }
  };

  const handleComment = async (timesheetId) => {
    if (!commentText.trim()) return;
    setSendingComment(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/timesheets/${timesheetId}/comment`, {
        method: 'POST', headers, body: JSON.stringify({ comment: commentText })
      });
      if (res.ok) {
        toast.success('Comment added');
        setCommentText('');
        fetchWeeklySummary();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to add comment');
      }
    } catch (err) { toast.error('Failed'); }
    finally { setSendingComment(false); }
  };

  const handleAdjust = async () => {
    if (!adjustModal || !adjustMinutes || !adjustReason.trim()) {
      toast.error('Please fill in adjusted minutes and reason');
      return;
    }
    setAdjusting(true);
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/timesheets/${adjustModal.timesheetId}/adjust`, {
        method: 'PUT', headers,
        body: JSON.stringify({ entry_id: adjustModal.entryId, adjusted_minutes: parseInt(adjustMinutes), reason: adjustReason })
      });
      if (res.ok) {
        toast.success('Entry adjusted');
        setAdjustModal(null);
        setAdjustMinutes('');
        setAdjustReason('');
        fetchWeeklySummary();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to adjust');
      }
    } catch (err) { toast.error('Failed'); }
    finally { setAdjusting(false); }
  };

  const filteredEmployees = useMemo(() => {
    if (!weeklySummary?.employees) return [];
    if (!searchQuery) return weeklySummary.employees;
    const q = searchQuery.toLowerCase();
    return weeklySummary.employees.filter(e => e.employee_name?.toLowerCase().includes(q));
  }, [weeklySummary, searchQuery]);

  const weekLabel = weeklySummary
    ? `${new Date(weeklySummary.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(weeklySummary.week_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';

  // ─── EMPLOYEE DETAIL VIEW ───
  if (selectedEmployee) {
    return (
      <div className="space-y-5" data-testid="employee-detail-view">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setSelectedEmployee(null)} data-testid="back-to-list">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{selectedEmployee.employee_name}</h1>
            <p className="text-sm text-gray-500">{weekLabel}</p>
          </div>
        </div>

        {/* Weekly Summary Bar */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-600">Weekly Total</span>
              <span className="font-bold text-gray-900">{formatMinutes(selectedEmployee.total_minutes)} / {formatMinutes(WEEKLY_TARGET)}</span>
            </div>
            <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${selectedEmployee.completion_pct >= 100 ? 'bg-green-500' : selectedEmployee.completion_pct >= 60 ? 'bg-blue-500' : 'bg-orange-400'}`}
                style={{ width: `${Math.min(selectedEmployee.completion_pct, 100)}%` }} />
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{selectedEmployee.days_logged} day{selectedEmployee.days_logged !== 1 ? 's' : ''} logged</span>
              <span>{selectedEmployee.completion_pct}% of target</span>
            </div>
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        {Object.keys(selectedEmployee.category_breakdown || {}).length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Category Breakdown</CardTitle></CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-2">
                {Object.entries(selectedEmployee.category_breakdown)
                  .sort((a, b) => b[1] - a[1])
                  .map(([cat, mins]) => {
                    const pct = Math.round((mins / (selectedEmployee.total_minutes || 1)) * 100);
                    return (
                      <div key={cat} className="flex items-center gap-3">
                        <span className="text-sm text-gray-700 w-36 truncate">{cat}</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-blue-500 rounded-full" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-xs text-gray-500 w-20 text-right">{formatMinutes(mins)} ({pct}%)</span>
                      </div>
                    );
                  })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Breakdown */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Calendar className="h-4 w-4" /> Daily Breakdown</h3>
          {(selectedEmployee.daily_breakdown || []).map(day => {
            const dayPct = Math.min((day.total_minutes / DAILY_TARGET) * 100, 100);
            const dayLabel = new Date(day.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
            const dayComments = (selectedEmployee.comments || []).filter(c => c.date === day.date);
            return (
              <Card key={day.date} data-testid={`day-card-${day.date}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-gray-800">{dayLabel}</span>
                    <div className="flex items-center gap-2">
                      <span className={`text-sm font-semibold ${dayPct >= 100 ? 'text-green-600' : 'text-gray-700'}`}>{formatMinutes(day.total_minutes)}</span>
                      <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className={`h-full rounded-full ${dayPct >= 100 ? 'bg-green-500' : dayPct >= 50 ? 'bg-blue-500' : 'bg-orange-400'}`}
                          style={{ width: `${dayPct}%` }} />
                      </div>
                    </div>
                  </div>

                  {/* Entries table */}
                  <div className="border rounded-lg overflow-hidden text-sm">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-2 font-medium text-gray-600">Task</th>
                          <th className="text-left p-2 font-medium text-gray-600">Category</th>
                          <th className="text-left p-2 font-medium text-gray-600">Project</th>
                          <th className="text-right p-2 font-medium text-gray-600">Time</th>
                          <th className="text-right p-2 font-medium text-gray-600 w-16"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {day.entries?.map((e, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="p-2">{e.task}</td>
                            <td className="p-2"><Badge variant="outline" className="text-xs">{e.category}</Badge></td>
                            <td className="p-2 text-gray-500">{e.project || '—'}</td>
                            <td className="p-2 text-right font-medium">
                              {formatMinutes(e.minutes)}
                              {e.adjusted_by && (
                                <Badge variant="outline" className="ml-1 text-[9px] border-amber-300 text-amber-600">Adj</Badge>
                              )}
                            </td>
                            <td className="p-2 text-right">
                              <Button variant="ghost" size="sm" className="h-7 px-2 text-gray-400 hover:text-blue-600"
                                onClick={() => { setAdjustModal({ timesheetId: day.id, entryId: e.id, task: e.task, currentMinutes: e.minutes }); setAdjustMinutes(String(e.minutes)); setAdjustReason(''); }}
                                data-testid={`adjust-btn-${day.date}-${i}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Existing comments for this day */}
                  {dayComments.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {dayComments.map((c, i) => (
                        <div key={c.id || i} className="text-sm bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                          <span className="font-medium text-amber-800">{c.by_name}:</span>{' '}
                          <span className="text-gray-700">{c.comment}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add comment for this day */}
                  <div className="mt-2 flex gap-2">
                    <Input
                      placeholder="Add a note or comment..."
                      value={commentText}
                      onChange={e => setCommentText(e.target.value)}
                      className="text-sm"
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleComment(day.id); } }}
                      data-testid={`comment-input-${day.date}`}
                    />
                    <Button size="sm" disabled={!commentText.trim() || sendingComment}
                      onClick={() => handleComment(day.id)} data-testid={`comment-send-${day.date}`}>
                      {sendingComment ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {(!selectedEmployee.daily_breakdown || selectedEmployee.daily_breakdown.length === 0) && (
            <Card><CardContent className="p-8 text-center text-gray-500">No timesheets logged this week</CardContent></Card>
          )}
        </div>

        {/* Adjust Dialog */}
        <Dialog open={!!adjustModal} onOpenChange={open => { if (!open) setAdjustModal(null); }}>
          <DialogContent className="sm:max-w-sm" data-testid="adjust-modal">
            <DialogHeader>
              <DialogTitle>Adjust Time Entry</DialogTitle>
            </DialogHeader>
            {adjustModal && (
              <div className="space-y-3 mt-2">
                <p className="text-sm text-gray-600">Task: <span className="font-medium">{adjustModal.task}</span></p>
                <p className="text-sm text-gray-600">Current: <span className="font-medium">{formatMinutes(adjustModal.currentMinutes)}</span></p>
                <div>
                  <label className="text-sm font-medium text-gray-700">New Minutes</label>
                  <Input type="number" min="0" max="720" value={adjustMinutes}
                    onChange={e => setAdjustMinutes(e.target.value)} data-testid="adjust-minutes-input" />
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700">Reason</label>
                  <Textarea value={adjustReason} onChange={e => setAdjustReason(e.target.value)}
                    placeholder="Why is this adjustment needed?" rows={2} data-testid="adjust-reason-input" />
                </div>
                <Button className="w-full" disabled={adjusting || !adjustReason.trim()}
                  onClick={handleAdjust} data-testid="confirm-adjust-btn">
                  {adjusting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Pencil className="h-4 w-4 mr-2" />}
                  Apply Adjustment
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ─── MAIN LIST VIEW ───
  return (
    <div className="space-y-6" data-testid="timesheet-review-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Team Timesheets</h1>
        <p className="text-gray-500">Review weekly time logs, add comments, and adjust entries</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg"><Users className="h-5 w-5 text-blue-600" /></div>
            <div>
              <p className="text-xs text-gray-500">Employees Logged</p>
              <p className="text-xl font-bold" data-testid="stat-logged">{stats.employees_logged || 0} / {stats.total_employees || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg"><Clock className="h-5 w-5 text-green-600" /></div>
            <div>
              <p className="text-xs text-gray-500">Total Hours (Week)</p>
              <p className="text-xl font-bold" data-testid="stat-hours">{formatMinutes(stats.total_minutes || 0)}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-amber-100 rounded-lg"><MessageSquare className="h-5 w-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-gray-500">Reviewed</p>
              <p className="text-xl font-bold" data-testid="stat-reviewed">{stats.reviewed_count || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg"><FileText className="h-5 w-5 text-purple-600" /></div>
            <div>
              <p className="text-xs text-gray-500">Days With Logs</p>
              <p className="text-xl font-bold" data-testid="stat-days">{stats.days_with_logs || 0}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Week Navigation + Search */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w - 1)} data-testid="prev-week">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center min-w-[180px]">
            <p className="font-semibold text-gray-800">{weekLabel}</p>
            {weekOffset === 0 && <p className="text-xs text-blue-600">Current Week</p>}
          </div>
          <Button variant="outline" size="icon" onClick={() => setWeekOffset(w => w + 1)} disabled={weekOffset >= 0} data-testid="next-week">
            <ChevronRight className="h-4 w-4" />
          </Button>
          {weekOffset !== 0 && (
            <Button variant="ghost" size="sm" onClick={() => setWeekOffset(0)} className="text-xs text-blue-600">Today</Button>
          )}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search employee..." value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)} className="pl-9" data-testid="search-employees" />
        </div>
      </div>

      {/* Employee Weekly Cards */}
      {loading ? (
        <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>
      ) : filteredEmployees.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-gray-500"><Users className="h-8 w-8 mx-auto mb-2 opacity-50" /><p>No timesheets found for this week</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {filteredEmployees.map(emp => {
            const pct = Math.min(emp.completion_pct, 100);
            const hasComments = (emp.comments || []).length > 0;
            return (
              <Card key={emp.employee_id}
                className="hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => { setSelectedEmployee(emp); setCommentText(''); }}
                data-testid={`emp-row-${emp.employee_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-medium text-sm">
                        {emp.employee_name?.charAt(0) || 'E'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{emp.employee_name}</p>
                        <p className="text-sm text-gray-500">{emp.days_logged} day{emp.days_logged !== 1 ? 's' : ''} logged</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {/* Category chips (top 3) */}
                      <div className="hidden md:flex gap-1">
                        {Object.entries(emp.category_breakdown || {})
                          .sort((a, b) => b[1] - a[1])
                          .slice(0, 3)
                          .map(([cat]) => (
                            <Badge key={cat} variant="outline" className="text-[10px]">{cat}</Badge>
                          ))}
                      </div>
                      <div className="text-right min-w-[100px]">
                        <p className={`text-sm font-semibold ${pct >= 100 ? 'text-green-600' : pct >= 60 ? 'text-gray-700' : 'text-orange-500'}`}>
                          {formatMinutes(emp.total_minutes)}
                        </p>
                        <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
                          <div className={`h-full rounded-full ${pct >= 100 ? 'bg-green-500' : pct >= 60 ? 'bg-blue-500' : 'bg-orange-400'}`}
                            style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-0.5">{emp.completion_pct}% of 40h</p>
                      </div>
                      {hasComments && (
                        <MessageSquare className="h-4 w-4 text-amber-500" />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TimesheetReview;
